import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE, extractError } from "../api";
import "./ApiKeysEditor.css"; // reuse modal chrome (.ake-*)
import "./AppVarsEditor.css"; // our extras (.ave-*)

// Mirrors backend validation — POSIX-style env var names.
const VALID_NAME_RE = /^[A-Z_][A-Z0-9_]{0,63}$/;

/**
 * AppVarsEditor — bulk-edit panel for per-app environment variables.
 *
 * Values are visible (unlike API Keys, which are secret) because these are
 * config knobs: base URLs, tenant IDs, feature flags, tunnel hostnames, etc.
 * Users explicitly need to READ them to debug "why isn't this working."
 *
 * UX model: edit a local copy; "Save" PUTs the entire set. Much simpler than
 * per-row save — the whole point of this panel is "set a handful of vars once,
 * then forget about them."
 *
 * Saving offers an "Apply now" flow that rebuilds the preview with the
 * current files — because Rob's feedback was emphatic that a variable change
 * with no obvious way to pick it up felt broken. Rather than auto-redeploy
 * (which could surprise the user mid-edit), we surface a clear opt-in CTA
 * after Save succeeds.
 */
export default function AppVarsEditor({ integrationName, onClose, onRequestRedeploy }) {
  // Rows is the editable list; keyed by stable index because the user might
  // have two rows with the same name mid-edit before they fix one.
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedTick, setSavedTick] = useState(0); // Triggers "Saved ✓" flash
  const [redeploying, setRedeploying] = useState(false);
  const [showRedeployCta, setShowRedeployCta] = useState(false);

  const fetchVars = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/integrations/${encodeURIComponent(integrationName)}/vars`);
      const data = await res.json();
      const vars = data.vars || {};
      const initialRows = Object.entries(vars).map(([name, value], i) => ({ id: i, name, value }));
      // Always give the user one empty row to type into — feels wrong to make
      // them click "Add variable" when the panel is empty.
      if (initialRows.length === 0) initialRows.push({ id: 0, name: "", value: "" });
      setRows(initialRows);
    } catch {
      setRows([{ id: 0, name: "", value: "" }]);
    } finally {
      setLoading(false);
    }
  }, [integrationName]);

  useEffect(() => { fetchVars(); }, [fetchVars]);

  const updateRow = (id, field, val) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  };

  const removeRow = (id) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length === 0 ? [{ id: Date.now(), name: "", value: "" }] : next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, { id: Date.now() + Math.random(), name: "", value: "" }]);
  };

  const isRowInvalid = (row) => row.name && !VALID_NAME_RE.test(row.name);
  const hasAnyInvalid = rows.some(isRowInvalid);
  const hasDuplicates = (() => {
    const seen = new Set();
    for (const r of rows) {
      const n = r.name.trim();
      if (!n) continue;
      if (seen.has(n)) return true;
      seen.add(n);
    }
    return false;
  })();

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      // Strip empty rows; send the rest.
      const vars = {};
      for (const r of rows) {
        const n = r.name.trim();
        if (!n) continue;
        vars[n] = r.value;
      }
      const res = await authFetch(`${API_BASE}/integrations/${encodeURIComponent(integrationName)}/vars`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vars }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(extractError(data, "Failed to save variables"));
      setSavedTick((t) => t + 1);
      // Surface the "apply now?" CTA whenever the save includes actual
      // values — but NOT when the only variables are blanks (user cleared
      // everything) since nothing meaningful changed for the container.
      const hasAny = Object.keys(vars).length > 0;
      if (hasAny && typeof onRequestRedeploy === "function") {
        setShowRedeployCta(true);
      }
      // Brief "Saved ✓" flash then clear
      setTimeout(() => setSavedTick(0), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const applyNow = async () => {
    if (typeof onRequestRedeploy !== "function") return;
    setRedeploying(true);
    try {
      await onRequestRedeploy();
      setShowRedeployCta(false);
      // Close the modal after a successful re-apply so the user sees the
      // preview updating rather than staring at the variables panel.
      setTimeout(() => onClose(), 800);
    } catch (err) {
      setError(`Redeploy failed: ${err.message}`);
    } finally {
      setRedeploying(false);
    }
  };

  return (
    <div className="ake-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ake-modal" style={{ width: 560 }}>
        <div className="ake-header">
          <h2>⚙️ App Variables: {integrationName}</h2>
          <button className="ake-close" onClick={onClose}>&times;</button>
        </div>
        <p className="ake-desc">
          Non-secret config values passed to your app as <code>process.env.X</code> at startup —
          base URLs, tenant IDs, feature flags, webhook endpoints, tunnel hostnames.
          For API keys and tokens, use the <strong>🔑 API Keys</strong> panel instead.
        </p>

        <div className="ave-restart-notice">
          ⚠️ Changes take effect on the <strong>next deploy</strong>. Make an edit in chat or
          redeploy after saving so the new values reach the container.
        </div>

        {error && <div className="ave-error">{error}</div>}

        {loading ? (
          <div className="ake-loading">Loading...</div>
        ) : (
          <>
            <div className="ave-rows">
              {rows.map((row) => (
                <div key={row.id} className="ave-row">
                  <input
                    type="text"
                    placeholder="NAME"
                    value={row.name}
                    onChange={(e) => updateRow(row.id, "name", e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                    className={isRowInvalid(row) ? "ave-invalid" : ""}
                    title={isRowInvalid(row) ? "Must be UPPER_SNAKE_CASE, start with letter/underscore" : undefined}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <input
                    type="text"
                    placeholder="value"
                    value={row.value}
                    onChange={(e) => updateRow(row.id, "value", e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    className="ave-row-remove"
                    onClick={() => removeRow(row.id)}
                    title="Remove this variable"
                    aria-label="Remove"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>

            <button className="ave-add-row" onClick={addRow}>
              + Add variable
            </button>

            {rows.length === 0 && (
              <div className="ave-empty">
                No variables yet. Add one above — e.g. <code>NOW_PLAYING_BASE_URL</code>.
              </div>
            )}

            {showRedeployCta && (
              <div className="ave-redeploy-cta">
                <div>
                  Variables saved. A redeploy is required for them to take effect.
                </div>
                <div className="ave-redeploy-cta-actions">
                  <button
                    className="ave-redeploy-skip"
                    onClick={() => setShowRedeployCta(false)}
                    disabled={redeploying}
                  >
                    Later
                  </button>
                  <button
                    className="ave-redeploy-now"
                    onClick={applyNow}
                    disabled={redeploying}
                  >
                    {redeploying ? "Redeploying…" : "Apply now"}
                  </button>
                </div>
              </div>
            )}

            <div className="ave-actions">
              {savedTick > 0 && <span className="ave-status-saved">Saved ✓</span>}
              <button
                className="ave-save"
                onClick={save}
                disabled={saving || hasAnyInvalid || hasDuplicates}
                title={
                  hasAnyInvalid ? "Fix invalid variable names first" :
                  hasDuplicates ? "Duplicate variable names" : undefined
                }
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
